import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { spassuCodeApiRef } from '../api';
import type { WorkerStatus } from '../api/types';

/**
 * Estados de apresentação do Frontier Worker. Deliberadamente quatro, e
 * deliberadamente com `unavailable` separado de `disconnected`: consulta que
 * falha ou é ilegível nunca pode ser exibida como "desconectado", porque isso
 * afirma um fato que não se tem (UT-034, UT-038).
 *
 * Esta política era do indicador do cabeçalho e o modal de escolha da via fazia
 * o oposto, transformando qualquer falha em desconexão. O hook existe para que
 * as duas superfícies contem a mesma história a partir da mesma classificação
 * (ADR-004).
 */
export type WorkerPresentation =
  | 'loading'
  | 'connected'
  | 'disconnected'
  | 'unavailable';

/** True só para payload que realmente diz se há worker conectado. */
function isReadableStatus(value: unknown): value is WorkerStatus {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    typeof (value as WorkerStatus).connected === 'boolean'
  );
}

export function presentationFor(
  status: WorkerStatus | null,
  failed: boolean,
): WorkerPresentation {
  if (failed) return 'unavailable';
  if (status === null) return 'loading';
  if (!isReadableStatus(status)) return 'unavailable';
  return status.connected ? 'connected' : 'disconnected';
}

export interface UseWorkerStatusOptions {
  /**
   * Consulta somente enquanto verdadeiro. Passar a falso devolve o estado a
   * `loading`, para que a próxima habilitação não exiba resposta antiga.
   */
  readonly enabled?: boolean;
  /**
   * Ausente = consulta única (usado pelo modal, que decide por abertura).
   * Presente = polling no intervalo informado (usado pelo indicador do
   * cabeçalho, que acompanha a sessão inteira).
   */
  readonly pollIntervalMs?: number;
}

export interface UseWorkerStatusResult {
  readonly presentation: WorkerPresentation;
  readonly status: WorkerStatus | null;
  readonly refresh: () => void;
}

export function useWorkerStatus(
  options: UseWorkerStatusOptions = {},
): UseWorkerStatusResult {
  const { enabled = true, pollIntervalMs } = options;
  const api = useApi(spassuCodeApiRef);
  const [status, setStatus] = useState<WorkerStatus | null>(null);
  const [failed, setFailed] = useState(false);
  const requestSequence = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const fetchStatus = useCallback(async () => {
    const sequence = ++requestSequence.current;
    try {
      const result = await api.getWorkerStatus();
      // Uma resposta mais nova já chegou; descarta esta obsoleta (UT-037).
      if (!mounted.current || sequence !== requestSequence.current) return;
      setStatus(result);
      setFailed(false);
    } catch {
      if (!mounted.current || sequence !== requestSequence.current) return;
      // Indisponível, NÃO desconectado.
      setFailed(true);
    }
  }, [api]);

  useEffect(() => {
    if (!enabled) {
      // Invalida qualquer resposta em voo e volta ao estado inicial, para que
      // habilitar de novo não exiba o resultado da vez anterior.
      requestSequence.current += 1;
      setStatus(null);
      setFailed(false);
      return undefined;
    }

    void fetchStatus();
    if (pollIntervalMs === undefined) return undefined;

    const interval = setInterval(() => void fetchStatus(), pollIntervalMs);
    return () => clearInterval(interval);
  }, [enabled, fetchStatus, pollIntervalMs]);

  return {
    presentation: presentationFor(status, failed),
    status,
    refresh: () => void fetchStatus(),
  };
}
